import React, { useEffect, useRef, useState } from 'react';
import ChatInterface from './ChatInterface';
import PremiumUpgradePrompt from './PremiumUpgradePrompt';

interface FloatingGuruChatProps {
  imageBase64?: string;
  imageMimeType?: string;
  initialAnalysis?: string;
  restoredMessages?: any[];
  onMessagesChange?: (messages: any[]) => void;
  // New props for unified button behavior
  hasAnalysis?: boolean;
  onAnalyze?: () => void;
  onReAnalyze?: () => void;
  isAnalyzing?: boolean;
  canAnalyze?: boolean; // Whether chart data is available for analysis

  raiseButtonOnMobile?: boolean;

  reAnalyzeCompletedAt?: number;

  // Premium subscription gate props
  canAccessAiAnalysis?: boolean;
  showUpgradePrompt?: boolean;
  onCloseUpgradePrompt?: () => void;
  onOpenUpgradeModal?: () => void;
  isAutoSubscribing?: boolean;
}

const FloatingGuruChat: React.FC<FloatingGuruChatProps> = ({
  imageBase64 = '',
  imageMimeType = '',
  initialAnalysis = '',
  restoredMessages = [],
  onMessagesChange,
  hasAnalysis = false,
  onAnalyze,
  onReAnalyze,
  isAnalyzing = false,
  canAnalyze = false,
  raiseButtonOnMobile = false,
  reAnalyzeCompletedAt = 0,
  // Premium subscription gate props
  canAccessAiAnalysis = true, // Default to true for backward compatibility
  showUpgradePrompt = false,
  onCloseUpgradePrompt,
  onOpenUpgradeModal,
  isAutoSubscribing = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const lastHandledReAnalyzeCompletedAtRef = useRef<number>(0);

  useEffect(() => {
    if (!reAnalyzeCompletedAt) return;
    if (reAnalyzeCompletedAt === lastHandledReAnalyzeCompletedAtRef.current) return;
    lastHandledReAnalyzeCompletedAtRef.current = reAnalyzeCompletedAt;
    if (isOpen) {
      setIsOpen(false);
      setIsMinimized(false);
    }
  }, [reAnalyzeCompletedAt, isOpen]);

  // Handle button click based on state
  const handleButtonClick = () => {
    console.log('[FloatingGuruChat] handleButtonClick', {
      hasAnalysis,
      canAnalyze,
      canAccessAiAnalysis,
      showUpgradePrompt,
    });

    if (!hasAnalysis && onAnalyze && canAnalyze) {
      // No analysis yet - trigger analysis (onAnalyze handles subscription gate)
      onAnalyze();
    } else if (hasAnalysis) {
      // Has analysis - check premium access before opening chat
      if (!canAccessAiAnalysis) {
        // Trigger the upgrade prompt via onAnalyze which handles the gate
        console.log('[FloatingGuruChat] No premium access, triggering onAnalyze for gate');
        if (onAnalyze) onAnalyze();
        return;
      }
      // Has premium access - open chat
      setIsOpen(true);
    }
  };

  // Determine button appearance and text
  const getButtonContent = () => {
    // Show activating subscription state
    if (isAutoSubscribing) {
      return {
        icon: (
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ),
        text: 'Activating Premium...',
        gradient: 'from-amber-500 to-orange-600',
        pulse: false
      };
    }

    if (isAnalyzing) {
      return {
        icon: (
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ),
        text: 'Analyzing...',
        gradient: 'from-purple-600 to-blue-600',
        pulse: false
      };
    }
    
    if (!hasAnalysis && canAnalyze) {
      return {
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        ),
        text: 'Analyze with Guru',
        gradient: 'from-purple-600 to-blue-600',
        pulse: true
      };
    }
    
    // Has analysis - show chat button
    return {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      text: 'Ask Guru',
      gradient: 'from-emerald-600 to-blue-600',
      pulse: true
    };
  };

  const buttonContent = getButtonContent();

  return (
    <>
      {/* Premium Upgrade Prompt */}
      {showUpgradePrompt && (
        <PremiumUpgradePrompt
          feature="aiAnalysis"
          onClose={onCloseUpgradePrompt || (() => {})}
          onUpgrade={() => {
            // Close the prompt first, then open the full upgrade modal
            if (onCloseUpgradePrompt) onCloseUpgradePrompt();
            if (onOpenUpgradeModal) onOpenUpgradeModal();
          }}
        />
      )}

      {/* Floating Smart Button */}
      {!isOpen && (canAnalyze || hasAnalysis) && (
        <button
          onClick={handleButtonClick}
          disabled={isAnalyzing || (!hasAnalysis && !canAnalyze)}
          className={`fixed right-6 sm:bottom-6 z-[200] group disabled:opacity-50 disabled:cursor-not-allowed ${
            raiseButtonOnMobile ? 'bottom-24' : 'bottom-10'
          }`}
          aria-label={hasAnalysis ? "Open Guru Chat" : "Analyze with Guru"}
        >
          {/* Pulse Animation Ring */}
          {buttonContent.pulse && (
            <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${buttonContent.gradient} opacity-75 group-hover:opacity-100 animate-pulse`}></div>
          )}
          
          {/* Main Button */}
          <div className={`relative flex items-center gap-2.5 bg-gradient-to-r ${buttonContent.gradient} hover:brightness-110 text-white px-4 py-2.5 rounded-full shadow-2xl transition-all duration-300 group-hover:scale-105 border border-white/20`}>
            {buttonContent.icon}
            <span className="font-semibold text-sm">{buttonContent.text}</span>
            
            {/* Sparkle Effect - only for chat mode */}
            {hasAnalysis && !isAnalyzing && (
              <div className="absolute -top-1 -right-1 w-2.5 h-2.5">
                <span className="flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-500"></span>
                </span>
              </div>
            )}
          </div>
        </button>
      )}

      {/* Floating Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-50 transition-all duration-300 ${
            isMinimized 
              ? 'bottom-6 right-6 w-80 h-16' 
              : 'bottom-4 right-4 w-[calc(100vw-2rem)] max-w-[420px] h-[calc(100vh-6rem)] max-h-[700px] sm:bottom-6 sm:right-6'
          }`}
        >
          <div className="bg-slate-900 border-2 border-emerald-500/30 rounded-2xl shadow-2xl shadow-emerald-500/20 overflow-hidden flex flex-col h-full w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-blue-600 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Guru Avatar */}
                <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30">
                  <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                
                <div>
                  <h3 className="text-white font-bold text-sm">TradeRewind Guru</h3>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  title={isMinimized ? "Maximize" : "Minimize"}
                >
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {isMinimized ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    )}
                  </svg>
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  title="Close"
                >
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Chat Content */}
            {!isMinimized && (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Disclaimer Banner */}
                <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-[10px] text-amber-200/90 leading-tight">
                    <strong className="font-semibold">Disclaimer:</strong> TradeVision Trading Gurus provides educational information only and is not financial advice. Trading involves substantial risk of loss. Always conduct your own research and consult with licensed financial advisors before making investment decisions.
                  </p>
                </div>

                {/* Chat Interface */}
                <div className="flex-1 overflow-hidden">
                  <ChatInterface
                    imageBase64={imageBase64}
                    imageMimeType={imageMimeType}
                    initialAnalysis={initialAnalysis}
                    restoredMessages={restoredMessages}
                    onMessagesChange={onMessagesChange || (() => {})}
                    onReAnalyze={onReAnalyze}
                    isReAnalyzing={isAnalyzing}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(FloatingGuruChat);
